import React, { useState } from 'react';
import './ModuleCatalog.css';
import PowerSupply from './modelFunc/components/cards/PowerSupply';
import UltraScaleComputer from './modelFunc/components/cards/UltraScaleComputer';
import ComputerSupervisor from './modelFunc/components/cards/ComputerSupervisor';
import VersalDataHandling from './modelFunc/components/cards/VersalDataHandling';
import MassMemory from './modelFunc/components/cards/MassMemory';
import EthernetExpresion from './modelFunc/components/cards/EthernetExpresion';
import Standard from './modelFunc/components/cards/Standard';
import GenericPeripheral from './modelFunc/components/cards/GenericPeripheral';
import ClearAll from './modelFunc/components/Button/ClearAll';


/**
 * ModuleCatalog component
 * 
 * This component renders a catalog of modules for a 9U rack.
 * 
 * @param {object} m - An object containing module data
 * @param {function} setSelectedModules - A function to set the selected modules
 * @param {object} rack9U - The current state of the 9U rack
 * @param {function} setRack9U - A function to update the state of the 9U rack
 * 
 * @returns {JSX.Element} The ModuleCatalog component
 */

const ModuleCatalog = ({ m, setSelectedModules, rack9U, setRack9U }) => {
  const [reset, setReset] = useState(false);

  /**
   * Ordenate function
   * 
   * This function reorders the modules in the rack to fill any gaps.
   */

  const ordenate = () => {
    if (rack9U?.modules?.[8] && !rack9U.modules[2]) {
      setRack9U((prevSelectedModules) => {
        prevSelectedModules.modules[2] = prevSelectedModules.modules[8];
        delete prevSelectedModules.modules[8];
        return prevSelectedModules;
      })
    } else if (rack9U?.modules?.[8] && !rack9U.modules[7]) {
      setRack9U((prevSelectedModules) => {
        prevSelectedModules.modules[7] = prevSelectedModules.modules[8];
        delete prevSelectedModules.modules[8];
        return prevSelectedModules;
      })
    } else if (rack9U.modules?.[7] && !rack9U.modules[2]) {

      setRack9U((prevSelectedModules) => {
        prevSelectedModules.modules[2] = prevSelectedModules.modules[7];
        delete prevSelectedModules.modules[7];
        return prevSelectedModules;
      })
    }
  }

    /**
   * Clear all selected modules
   * 
   * This function clears all selected modules and resets the rack state.
   */

  const clearModules = () => {
    setRack9U({ rackModel: rack9U.rackModel, modules: [] });
    setSelectedModules([]);
    setReset(true);  // Trigger reset for all counts
    setTimeout(() => setReset(false), 0);  // Reset reset state after clearing
  };

 /**
   * Handle base power. Always in first two positions. In this case, the second gets a blind panel.
   * 
   * This function sets the base power module in the rack.
   */

  const handleBasePower = () => {
    setRack9U((prevRack9U) => {
      prevRack9U.modules[0] = { ...m[1], positionX: 0 };
      prevRack9U.modules[1] = { ...m[8], positionX: 30.5 }; //blind panel
      return prevRack9U;
    });
    setSelectedModules({ ...rack9U });
  };

   /**
   * Handle redundant power. Always in first two positions
   * 
   * This function sets redundant power in the rack.
   */

  const handleRedundantPower = () => {

    setRack9U((prevRack9U) => {
      prevRack9U.modules[0] = { ...m[1], positionX: 0 };
      prevRack9U.modules[1] = { ...m[1], positionX: 30.5 };
      return prevRack9U;
    });
    setSelectedModules({ ...rack9U });
  }

/**
   * Handle UltraScale plus Computer. Always in positions 6 and 7. In this case, the second gets a blind panel.
   * 
   * This function sets the nominal UltraScale plus Computer in the rack.
   */

  const handleNominalUltraScale = () => {

    setRack9U((prevRack9U) => {
      prevRack9U.modules[5] = { ...m[5], positionX: 30.5 * 5 };
      prevRack9U.modules[6] = { ...m[8], positionX: 30.5 * 6 }; //blind panel
      return prevRack9U;
    });
    setSelectedModules({ ...rack9U });
  }

  
/**
   * Handle UltraScale plus Computer. Always in positions 6 and 7.
   * 
   * This function sets the redundant UltraScale plus Computer in the rack.
   */
  const handleRedundantUltraScale = () => {

    setRack9U((prevRack9U) => {
      prevRack9U.modules[5] = { ...m[5], positionX: 30.5 * 5 };
      prevRack9U.modules[6] = { ...m[5], positionX: 30.5 * 6 };
      return prevRack9U;
    });
    setSelectedModules({ ...rack9U });
  }

/**
   * Handle Computer Supervisor. Always in positions 4 and 5. In this case, the second gets a blind panel.
   * 
   * This function sets the nominal Computer Supervisor in the rack.
   */

  const handleNominalSupervisor = () => {
    setRack9U((prevRack9U) => {
      prevRack9U.modules[3] = { ...m[6], positionX: 30.5 * 3 };
      prevRack9U.modules[4] = { ...m[8], positionX: 30.5 * 4 }; //blind panel
      return prevRack9U;
    });

    setSelectedModules({ ...rack9U });
  }

  /**
   * Handle Computer Supervisor. Always in positions 4 and 5.
   * 
   * This function sets the redundant Computer Supervisor in the rack.
   */

  const handleRedundantSupervisor = () => {
    setRack9U((prevRack9U) => {
      prevRack9U.modules[3] = { ...m[6], positionX: 30.5 * 3 };
      prevRack9U.modules[4] = { ...m[6], positionX: 30.5 * 4 };
      return prevRack9U;
    });
    setSelectedModules({ ...rack9U });
  }

   /**
   * The rest handles all the modules that might be in positions 3, 8 and 9:
   * Versal Data Handling Computer
   * Mass Memory
   * Ethernet Expansion
   * Standard I/Os
   * Generic Peripheral Board
   */

  const handleVersal = (n) => {

    if (!rack9U.modules[2]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[2] = { ...m[3], positionX: 30.5 * 2 }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    } else if (!rack9U.modules[7]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[7] = { ...m[3], positionX: 30.5 * 7 }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    } else if (!rack9U.modules[8]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[8] = { ...m[3], positionX: 30.5 * 8 }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    }
    setSelectedModules({ ...rack9U });
  }

  const handleVersalDecrement = () => {
    if (rack9U.modules[8] && rack9U.modules[8].id === m[3].id) {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[8]
        return prevRack9U;
      });
    } else if (rack9U.modules[7] && rack9U.modules[7].id === m[3].id) {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[7];
        return prevRack9U;
      });
    } else if (rack9U.modules[2] && rack9U.modules[2].id === m[3].id) {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[2];
        return prevRack9U;
      });
    }
    ordenate();
    setSelectedModules({ ...rack9U });

  }

  const handleMassMemory = (n) => {
    if (!rack9U.modules[2]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[2] = { ...m[0], positionX: 30.5 * 2 ,gb:n}
        return prevRack9U;
      });
    } else if (!rack9U.modules[7]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[7] = { ...m[0], positionX: 30.5 * 7 ,gb:n}
        return prevRack9U;
      });
    } else if (!rack9U.modules[8]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[8] = { ...m[0], positionX: 30.5 * 8 ,gb:n}
        return prevRack9U;
      });
    }
    setSelectedModules({ ...rack9U });
  }

  const handleExpansionEthernet = (c) => {
    if (c) {
      if (!rack9U.modules[2]) {
        setRack9U((prevRack9U) => {
          prevRack9U.modules[2] = { ...m[7], positionX: 30.5 * 2 }
          return prevRack9U;
        });
      } else if (!rack9U.modules[7]) {
        setRack9U((prevRack9U) => {
          prevRack9U.modules[7] = { ...m[7], positionX: 30.5 * 7 }
          return prevRack9U;
        });
      } else if (!rack9U.modules[8]) {
        setRack9U((prevRack9U) => {
          prevRack9U.modules[8] = { ...m[7], positionX: 30.5 * 8 }
          return prevRack9U;
        });
      }
      setSelectedModules({ ...rack9U });
    } else {
      if (rack9U.modules[8] && rack9U.modules[8].id === m[7].id) {
        setRack9U((prevRack9U) => {
          delete prevRack9U.modules[8]
          return prevRack9U;
        });
      } else if (rack9U.modules[7] && rack9U.modules[7].id === m[7].id) {
        setRack9U((prevRack9U) => {
          delete prevRack9U.modules[7];
          return prevRack9U;
        });
      } else if (rack9U.modules[2] && rack9U.modules[2].id === m[7].id) {
        setRack9U((prevRack9U) => {
          delete prevRack9U.modules[2];
          return prevRack9U;
        });
      }
      ordenate();
      setSelectedModules({ ...rack9U });
    }
  }
  const handleStdio = (n) => {
    if (!rack9U.modules[2]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[2] = { ...m[2],type:n }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    } else if (!rack9U.modules[7]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[7] = { ...m[2],type:n }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    } else if (!rack9U.modules[8]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[8] = { ...m[2],type:n}
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    }
    setSelectedModules({ ...rack9U });
  }
  const handleStdioDecrement16 = () => {
    if (rack9U.modules[8] && rack9U.modules[8].id === m[2].id && rack9U.modules[8].type==='16 Analog Inputs') {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[8]
        return prevRack9U;
      });
    } else if (rack9U.modules[7] && rack9U.modules[7].id === m[2].id && rack9U.modules[7].type==='16 Analog Inputs') {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[7];
        return prevRack9U;
      });
    } else if (rack9U.modules[2] && rack9U.modules[2].id === m[2].id && rack9U.modules[2].type==='16 Analog Inputs') {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[2];
        return prevRack9U;
      });
    }
    ordenate();
    setSelectedModules({ ...rack9U });

  }
  const handleStdioDecrement8 = () => {
 

    if (rack9U.modules[8] && rack9U.modules[8].id === m[2].id && rack9U.modules[8]?.type==='8 Analog Outputs') {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[8]
        return prevRack9U;
      });
    } else if (rack9U.modules[7] && rack9U.modules[7].id === m[2].id && rack9U.modules[7]?.type==='8 Analog Outputs') {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[7];
        return prevRack9U;
      });
    } else if (rack9U.modules[2] && rack9U.modules[2].id === m[2].id && rack9U.modules[2]?.type==='8 Analog Outputs') {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[2];
        return prevRack9U;
      });
    }
    ordenate();
    setSelectedModules({ ...rack9U });

  }

  const handleGeneric = (n) => {

    if (!rack9U.modules[2]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[2] = { ...m[4], positionX: 30.5 * 2 }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    } else if (!rack9U.modules[7]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[7] = { ...m[4], positionX: 30.5 * 7 }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    } else if (!rack9U.modules[8]) {
      setRack9U((prevRack9U) => {
        prevRack9U.modules[8] = { ...m[4], positionX: 30.5 * 8 }
        return prevRack9U;
      });
      setSelectedModules({ ...rack9U });
      return true;
    }
    setSelectedModules({ ...rack9U });
  }
  const handleGenericDecrement = () => {
    if (rack9U.modules[8] && rack9U.modules[8].id === m[4].id) {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[8]
        return prevRack9U;
      });

    } else if (rack9U.modules[7] && rack9U.modules[7].id === m[4].id) {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[7];
        return prevRack9U;
      });

    } else if (rack9U.modules[2] && rack9U.modules[2].id === m[4].id) {
      setRack9U((prevRack9U) => {
        delete prevRack9U.modules[2];
        return prevRack9U;
      });

    }
    ordenate();
    setSelectedModules({ ...rack9U });
  }

  return (
    <div className="module-catalog">
      <h2 className='ConfigurationH2'>Configurations</h2>

      <div className='containerCatalogo'>

        <PowerSupply handleBasePower={handleBasePower} handleRedundantPower={handleRedundantPower} />
        <UltraScaleComputer handleNominalUltraScale={handleNominalUltraScale} handleRedundantUltraScale={handleRedundantUltraScale} />
        <ComputerSupervisor handleNominalSupervisor={handleNominalSupervisor} handleRedundantSupervisor={handleRedundantSupervisor} />
        <VersalDataHandling handleVersal={handleVersal} handleVersalDecrement={handleVersalDecrement} reset={reset}  />
        <MassMemory handleMassMemory={handleMassMemory} />
        <EthernetExpresion handleExpansionEthernet={handleExpansionEthernet} />
        <Standard handleStdio={handleStdio} handleStdioDecrement16={handleStdioDecrement16} handleStdioDecrement8={handleStdioDecrement8} reset={reset} />
        <GenericPeripheral
          handleGeneric={handleGeneric}
          handleGenericDecrement={handleGenericDecrement}
          reset={reset}  
        />
        <div style={{ marginBottom: '20px',  justifyContent: 'flex-end', position:'relative', left:'35%'}}>
          <ClearAll onClear={clearModules} />
        </div>

      </div>
    </div>
  );
};

export default ModuleCatalog;